/**
 * Order history — the target of the footer's "Track Order" link.
 *
 * This is a real page, not a shell: it reads the same orders-store the account dashboard
 * does and renders through the shared OrderHistoryList, so the two can never disagree about
 * a status or a total. /orders already existed but is the post-checkout CONFIRMATION screen
 * for a single order, which is why it was never a valid "Track Order" target.
 *
 * Deliberately lighter than the dashboard's panel: no realtime subscription, no
 * BroadcastChannel, no wishlist or address editing. Those belong to /account, which stays
 * the place you manage things; this URL answers one question ("where is my order") for
 * someone arriving from the footer.
 *
 * Prerendering note: this route is prerendered like every other client-only route, so the
 * static HTML captures the FIRST render -- when useCurrentUser() still reports
 * isLoading: true. The loading branch therefore ships as the baked markup, which is why it
 * is one honest line of text rather than a skeleton. See the /collections prerender trap in
 * the project notes: a skeleton in the first render is a skeleton in the deployed HTML.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Lock, ShoppingBag } from "lucide-react";
import { Reveal } from "@/components/common/reveal";
import { siteConfig } from "@/config/site";
import { useCurrentUser } from "@/hooks/use-auth";
import { OrderHistoryList } from "@/features/orders/components/order-history-list";
import {
  getUserOrders,
  fetchUserOrdersFromSupabase,
  type UserOrder,
} from "@/features/orders/store/orders-store";

export const Route = createFileRoute("/account/orders")({
  head: () => ({
    meta: [
      { title: `My Orders — ${siteConfig.name}` },
      // noindex, follow -- consistent with every other /account route. The content is
      // per-user and worthless to a crawler, but the URL is linked from the footer on every
      // page, so a Disallow in robots.txt would leave it eligible for URL-only indexing with
      // the directive unreadable. See the longer note in cart.tsx.
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: OrdersHistoryPage,
});

function OrdersHistoryPage() {
  const { user, isAuthenticated, isLoading } = useCurrentUser();
  const [orders, setOrders] = useState<UserOrder[]>([]);

  useEffect(() => {
    if (!user) {
      setOrders([]);
      return;
    }

    // Local cache first so the list paints immediately, then reconcile against Supabase.
    // fetchUserOrdersFromSupabase already falls back to the local orders on a query error,
    // so a failed round trip degrades to the cached list rather than an empty one.
    const cached = getUserOrders(user.id, user.email, user.phone);
    if (cached.length > 0) setOrders(cached);

    let active = true;
    fetchUserOrdersFromSupabase(user.id, user.email)
      .then((remote) => {
        if (active) setOrders(remote);
      })
      .catch(() => {
        // Keep whatever the local cache produced.
      });

    return () => {
      active = false;
    };
  }, [user]);

  return (
    <div className="pb-28 pt-24 md:pt-28">
      <div className="section-container">
        <Reveal>
          <p className="eyebrow">Account</p>
          <h1 className="mt-6 max-w-3xl">My orders</h1>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Every order placed with this account, newest first, with its current status.
          </p>
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-12 border bg-card p-6">
            {isLoading ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                Checking your session…
              </p>
            ) : !isAuthenticated ? (
              <div className="py-12 text-center">
                <Lock className="mx-auto h-10 w-10 text-muted-foreground/60" strokeWidth={1.25} />
                <p className="mt-3 font-display text-base">Sign in to see your orders</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Order history is tied to your account, so we need to know who you are first.
                </p>
                <Link
                  to="/account/login"
                  className="mt-5 inline-flex min-h-11 items-center bg-primary px-6 text-xs tracking-wider text-primary-foreground uppercase hover:opacity-90"
                >
                  Sign in
                </Link>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between border-b pb-4">
                  <h2 className="flex items-center gap-2 font-display text-xl">
                    <ShoppingBag className="h-5 w-5 text-primary" strokeWidth={1.25} />
                    Orders ({orders.length})
                  </h2>
                  <Link to="/account" className="text-xs text-primary hover:underline">
                    Account settings
                  </Link>
                </div>
                <OrderHistoryList orders={orders} />
              </>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
